import { Injectable, signal } from "@angular/core";

export type AppTheme = "light" | "dark";

/**
 * Přepínání světlého / tmavého motivu.
 *
 * Motiv se drží v atributu `data-bs-theme` na <html> — tím se aktivuje
 * nativní dark mode Bootstrapu 5.3 i naše přepisy v colors.scss /
 * dark-theme.scss. Volba se persistuje v localStorage; inline skript
 * v index.html ji aplikuje ještě před startem Angularu (žádný záblesk
 * světlého motivu).
 */
@Injectable({ providedIn: "root" })
export class ThemeService {
  private static readonly KEY = "ems_theme";
  /** Délka přechodu mezi motivy [ms] — drž v sync s CSS (.theme-anim, ::view-transition). */
  private static readonly ANIM_MS = 320;

  /** Aktuální motiv (signal — jde číst přímo v šablonách). */
  readonly theme = signal<AppTheme>(ThemeService.initial());

  private animTimer: number | null = null;

  constructor() {
    // index.html už atribut zpravidla nastavil; tady se stav jen sladí
    // (a pokryje případ, kdy inline skript neproběhl). Bez animace.
    this.apply(this.theme());
  }

  get isDark(): boolean {
    return this.theme() === "dark";
  }

  toggle(): void {
    this.set(this.isDark ? "light" : "dark");
  }

  set(theme: AppTheme): void {
    if (theme === this.theme()) {
      return;
    }
    this.theme.set(theme);
    try {
      localStorage.setItem(ThemeService.KEY, theme);
    } catch {}
    this.applyAnimated(theme);
  }

  /**
   * Plynulý cross-fade celé stránky. Primárně View Transitions API
   * (Chrome/Edge/Safari — jeden GPU snímek, žádné per-element transitions);
   * fallback je dočasná třída `theme-anim` na <html>, která pustí CSS
   * transition barev (viz styles.scss). Při prefers-reduced-motion se
   * přepíná okamžitě.
   */
  private applyAnimated(theme: AppTheme): void {
    const reducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (reducedMotion) {
      this.apply(theme);
      return;
    }

    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => unknown;
    };
    if (typeof doc.startViewTransition === "function") {
      doc.startViewTransition(() => this.apply(theme));
      return;
    }

    const root = document.documentElement;
    root.classList.add("theme-anim");
    this.apply(theme);
    if (this.animTimer !== null) {
      window.clearTimeout(this.animTimer);
    }
    this.animTimer = window.setTimeout(() => {
      root.classList.remove("theme-anim");
      this.animTimer = null;
    }, ThemeService.ANIM_MS);
  }

  private apply(theme: AppTheme): void {
    document.documentElement.setAttribute("data-bs-theme", theme);
    // Barva systémové lišty (PWA / mobil) ladí s motivem.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "dark" ? "#14181f" : "#1976d2");
  }

  private static initial(): AppTheme {
    try {
      const saved = localStorage.getItem(ThemeService.KEY);
      if (saved === "dark" || saved === "light") {
        return saved;
      }
    } catch {}
    return "light";
  }
}
